<template>
    <section class="grid-card-section">
        <div class="grid-card-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />

            <div class="grid-card-section__grid">
                <article v-for="(cell, idx) in props.item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="feature-card"
                    :class="`feature-card--bg-${idx % 4}`" :style="getCellStyle(cell)" @click="handleCellClick(cell)">
                    <MediaCard class="feature-card__media" :url="getCellImage(cell)" :name="getCellTitle(cell)" />

                    <div class="feature-card__body">
                        <h3 v-if="getCellTitle(cell)" class="feature-card__title">{{ getCellTitle(cell) }}</h3>
                        <p v-if="getCellDesc(cell)" class="feature-card__desc">{{ getCellDesc(cell) }}</p>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, getNumWithRecord, jumpToUrl } from './index.js';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellDesc(cell: unknown): string {
    return getStrWithRecord(cell, 'subTitle')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}
function getCellCol(cell: unknown): number {
    return getNumWithRecord(cell, 'col', 1)
}
function getCellRow(cell: unknown): number {
    return getNumWithRecord(cell, 'row', 1)
}

/** 根据 col/row 计算内联样式（桌面端），移动端统一跨1列 */
function getCellStyle(cell: unknown): Record<string, string> {
    const col = getCellCol(cell)
    const row = getCellRow(cell)
    return {
        '--card-col-span': String(Math.min(col, 2)),
        '--card-row-span': String(row),
    } as Record<string, string>
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    jumpToUrl(url)
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

$bg-blue: #e8edf5;
$bg-gray: #d9dbdf;
$bg-peach: #f5d5c4;

.grid-card-section {
    width: 100%;
}

.grid-card-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

/* 手机端 1列，桌面端 2列，使用 CSS Grid 自动行高 */
.grid-card-section__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    grid-auto-flow: dense;

    @media (min-width: $breakpoint-md) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.25rem;
        grid-auto-rows: minmax(260px, auto);
    }
}

.feature-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 1rem;
    overflow: hidden;
    padding: 1.5rem;
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    min-height: 180px;
    position: relative;

    /* 桌面端跨列/跨行 */
    @media (min-width: $breakpoint-md) {
        grid-column: span var(--card-col-span, 1);
        grid-row: span var(--card-row-span, 1);
        padding: 2.5rem 2rem;
        min-height: 260px;
        border-radius: 1.125rem;
    }

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);

        .feature-card__media img {
            transform: scale(1.04);
        }
    }

    /* 背景色变体 */
    &--bg-0 {
        background: $bg-blue;
    }

    &--bg-1 {
        background: $bg-gray;
    }

    &--bg-2 {
        background: $bg-blue;
    }

    &--bg-3 {
        background: $bg-peach;
    }

    &__media {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.4s ease;
            filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.08));
        }
    }

    &__body {
        flex: 1;
        min-width: 0;
        position: absolute;
        left: 50%;
        top: 1.25rem;
        right: 1rem;
    }

    &__title {
        font-size: 1.375rem;
        font-weight: 800;
        color: $color-text;
        margin: 0 0 0.5rem;
        line-height: 1.3;
        letter-spacing: -0.01em;

        @media (min-width: $breakpoint-md) {
            font-size: 1.75rem;
            margin-bottom: 0.75rem;
        }
    }

    &__desc {
        font-size: 0.75rem;
        color: $color-text-secondary;
        margin: 0;
        line-height: 1.6;

        @media (min-width: $breakpoint-md) {
            font-size: $font-size-sm;
        }
    }
}

/* 手机端小屏幕 */
@media (max-width: 380px) {
    .feature-card {
        padding: 1.25rem;
        min-height: 150px;

        &__body {
            left: 50%;
            top: 0.5rem;
            right: 1rem;
        }

        &__title {
            font-size: 1.125rem;
        }

        &__desc {
            font-size: 0.6875rem;
        }
    }
}
</style>
